import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, BookOpen, ClockCountdown, Target, Certificate } from '@phosphor-icons/react';

const exams = [
  { name: 'iTEP', full: 'International Test of English Proficiency', desc: 'Widely accepted by colleges for English proficiency assessment. We provide targeted preparation with practice tests and strategy coaching.', features: ['Grammar & Vocabulary', 'Reading Comprehension', 'Listening Skills', 'Writing Practice', 'Speaking Preparation'] },
  { name: 'Accuplacer', full: 'College Board Placement Test', desc: 'Used by many colleges for course placement. Our preparation ensures students place into the right courses from day one.', features: ['Reading Skills', 'Writing Skills', 'Math Fundamentals', 'Quantitative Reasoning', 'Advanced Algebra'] },
  { name: 'CLBPT / CLBP', full: 'Canadian Language Benchmarks Placement Test', desc: 'Essential for Canadian college admissions. We prepare students for all aspects of the Canadian language benchmarks assessment.', features: ['Listening Assessment', 'Speaking Assessment', 'Reading Assessment', 'Writing Assessment', 'CLB Level Targeting'] },
  { name: 'IELTS', full: 'International English Language Testing System', desc: 'The most widely recognized English test globally. Our IELTS prep covers all four modules with proven score-improvement strategies.', features: ['Academic Reading', 'Academic Writing', 'Listening Modules', 'Speaking Interview', 'Band Score Strategy'] },
];

export default function ExamPrep() {
  return (
    <div data-testid="exam-prep-page">
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <p className="text-blue-600 font-medium text-sm uppercase tracking-widest mb-3">Exam Preparation</p>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight mb-6">Prepare. Practice. Pass.</h1>
              <p className="text-base lg:text-lg text-gray-600 leading-relaxed mb-8">
                Don't let an exam stand between you and your dream college. We offer expert preparation support for all major admission and English proficiency tests.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: BookOpen, label: 'Study Materials' },
                  { icon: ClockCountdown, label: 'Timed Practice' },
                  { icon: Target, label: 'Score Targets' },
                  { icon: Certificate, label: 'Mock Exams' },
                ].map(f => (
                  <div key={f.label} className="flex items-center gap-2.5 p-3 bg-white border border-gray-200 rounded-lg">
                    <f.icon size={20} className="text-blue-600 shrink-0" />
                    <span className="text-sm font-medium text-gray-700">{f.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-6">
              <img src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxzdHVkZW50JTIwc3R1ZHlpbmd8ZW58MHx8fHwxNzc2MzY1NTY0fDA&ixlib=rb-4.1.0&q=85" alt="Exam prep" className="rounded-lg shadow-lg w-full object-cover max-h-[440px]" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">Exams We Prepare You For</h2>
          </div>
          <div className="space-y-12">
            {exams.map((exam, i) => (
              <div key={exam.name} className="border border-gray-200 rounded-xl p-8 hover:shadow-lg transition-shadow" data-testid={`exam-${exam.name.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                  <div className="lg:col-span-8">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-full uppercase tracking-wider">{exam.name}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Libre Baskerville', serif" }}>{exam.full}</h3>
                    <p className="text-gray-600 leading-relaxed">{exam.desc}</p>
                  </div>
                  <div className="lg:col-span-4">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Preparation Covers</p>
                    <div className="space-y-2">
                      {exam.features.map(f => (
                        <div key={f} className="flex items-center gap-2">
                          <CheckCircle size={16} weight="fill" className="text-blue-500 shrink-0" />
                          <span className="text-sm text-gray-600">{f}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 tracking-tight mb-4">Need Exam Support?</h2>
          <p className="text-gray-600 mb-8">Whether you're a student preparing for an exam or a recruiter with students who need test preparation, we're here to help.</p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-blue-800 text-white px-8 py-3 rounded-md font-medium hover:bg-blue-700 transition-all" data-testid="exam-contact-btn">
            Get in Touch <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
